import React, { useEffect, useState } from 'react';
import { ENTER_NAME, CREATE_CLASS, CLASS_NAME, ASSIGN_COLOR, MODEL_CLASS, FIELD_CLASS, TYPE } from '../ISRConstants';
import { XIcon } from 'assests/GmsIcons';
import { COLORS, validateClassAndSubmodelNames } from '../ISRHelper';
import { TClassProject, TFieldClass } from '../Pages/DefineModel/model';
import { createClass, updateClass } from '../Pages/DefineModel/ModelServices/ModalServices';
import { toast } from 'Components/DesignSystem/Toast/useToast';

type TFieldProps = {
    id: string;
    onClose: () => void;
    projectId?: string;
    selectedClass: TFieldClass;
    setRefreshCounter: React.Dispatch<React.SetStateAction<number>>;
}

const CreateClassDialog = ({id, onClose, projectId, setRefreshCounter, selectedClass}: TFieldProps) => {
    const [showColors, setShowColors] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<number>(0);
    const initializeClass = { project: projectId ?? '', class: { id: null, name: '', type: '', colour: COLORS[0] },}
    const [disableButton, setDisableButton] = useState<boolean>(false);
    const [classProject, setClassProject] = useState<TClassProject>(initializeClass);

    useEffect(() => {
        if (selectedClass?.id) {
            setClassProject((previousProject) => {
                return { ...previousProject, class: selectedClass };
            });
        }
    }, [selectedClass]);
    

    const handleClick = () => {
        setShowColors((prev) => prev = !prev);
    };

    const handleSelection = (index: number) => {
        setSelectedItem(index);
        setShowColors((prev) => prev = !prev);
        setClassProject((previousProject) => {
            const updatedClass = { ...previousProject.class, colour: COLORS[index]};
            return { ...previousProject, class: updatedClass };
        });
    }
    
    const handleOnCreateClick = () => {
        if (validateClassAndSubmodelNames(classProject?.class?.name)) {
            if (selectedClass?.id) {
                updateProjectClass();
            } else {
                createProjectClass();
            }
        }
    };

    const handleFieldNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.value;
        setClassProject((previousProject) => {
            const updatedClass = { ...previousProject.class, name: name };
            return { ...previousProject, class: updatedClass };
        });
    };

    const handleFieldTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const type = event.target.value
        setClassProject((previousProject) => {
            const updatedClass = { ...previousProject.class, type: type };
            return { ...previousProject, class: updatedClass };
        });
    };

    const createProjectClass = async () => {
        try {
            setDisableButton(true);
            const responseData = await createClass(classProject);
            console.log(responseData);
            if (responseData?.done) {
                toast.success(responseData?.message);
                setRefreshCounter((prev) => prev = prev + 1);
                setClassProject(initializeClass);
            } else {
                toast.error(responseData?.message);
            }
        } catch (error) {
            console.log('Error', error);
        } finally {
            setDisableButton(false);
        }
    }

    const updateProjectClass = async () => {
        try {
            setDisableButton(true);
            const responseData = await updateClass(classProject);
            console.log(responseData);
            if (responseData?.done) {
                toast.success(responseData?.message);
                setRefreshCounter((prev) => prev = prev + 1);
            } else {
                toast.error(responseData?.message);
            }
        } catch (error) {
            console.log('Error', error);
        } finally {
            setDisableButton(false);
        }
    }

    return (
        <div id={id} className={`tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-gray-800 tw-bg-opacity-50 tw-z-50`}>
            <div id={`${id}-content`} className={`tw-bg-white tw-rounded tw-shadow-md tw-w-[38%] tw-h-auto`}>
                <div id={`${id}-header`} className='tw-flex tw-justify-between tw-items-center'>
                    <h2 id={`${id}-title`} className={`tw-text-sm tw-font-semibold tw-my-3 tw-px-3 `}>
                        {CREATE_CLASS}
                    </h2>
                    <button id={`${id}-close-btn`} data-testid={`${id}-close-btn`} aria-label="Close" className='tw-mr-2' onClick={onClose}>
                        <XIcon />
                    </button>
                </div>
                <div id={`${id}-divider`} className={`tw-box-border tw-w-auto tw-h-px tw-border-t-[1px] tw-border-solid tw-border-[#D1D1D6]`} />  
                <div id={`${id}-form`} className={`tw-px-3 tw-my-3`}>
                    <div className={`tw-flex tw-gap-3 tw-items-center`}>
                        <div id={`${id}-name-section`} className='tw-w-[50%] tw-pe-2 tw-pt-1'>
                            <label id={`${id}-name-label`} className={`tw-text-sm tw-block tw-ps-1 tw-mb-1.5`}>
                                {CLASS_NAME}
                            </label>
                            <div id={`${id}-name-input-container`} className="tw-relative">
                                <input data-testid="components-createclassdialog-input-1"
                                    id={`${id}-name-input`}
                                    value={classProject?.class?.name ?? ''}
                                    type="text"
                                    onChange={handleFieldNameChange}
                                    placeholder={ENTER_NAME}
                                    className='className={`tw-w-auto tw-min-w-full tw-border tw-border-[#EBECF0] tw-text-sm tw-block tw-py-2 tw-px-3 tw-m-1 tw-bg-[#fff] tw-bg-clip-padding focus:tw-text-gray-700 tw-rounded-[4px] tw-flex-grow tw-font-normal tw-shadow-none`}'
                                />
                            </div>
                        </div>
                        <div id={`${id}-type-section`} className='tw-w-[25%]'>
                            <label id={`${id}-type-label`} className={`tw-text-sm tw-block tw-ps-1`}>
                                {TYPE}
                            </label>
                            <select data-testid="components-createclassdialog-select-2"
                                id={`${id}-type-select`}
                                className="drop-down-dm"
                                value={classProject?.class?.type}
                                onChange={handleFieldTypeChange}
                            >
                                <option id={`${id}-type-default`}>
                                    Select Type
                                </option>
                                {[MODEL_CLASS, FIELD_CLASS].map((item, index) => (
                                    <option
                                        id={`${id}-type-option-${index}`}
                                        value={item}
                                        key={item + index}
                                    >
                                        {item}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div id={`${id}-color-section`} className='tw-w-[15%]'>
                            <label id={`${id}-color-label`} className='tw-text-sm tw-block'>
                                {ASSIGN_COLOR}
                            </label>
                            <button data-testid="components-createclassdialog-button-3"
                                aria-label="Pick colour"
                                id={`${id}-color-picker-btn`}
                                style={{ backgroundColor: classProject?.class?.colour }}
                                onClick={handleClick}
                                className={`tw-w-11 tw-h-9 tw-px-2.5 tw-py-2 tw-mt-1.5 tw-rounded tw-border-2 tw-border-white tw-border-opacity-50 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex`}
                            />
                        </div>
                    </div>
                    <p id={`${id}-name-hint`} className="tw-text-xs tw-text-[#6B778C] tw-mt-1 tw-ps-1 tw-w-[50%] tw-pe-2">
                        Class name must be uppercase, alphanumeric, and cannot start with a number.
                    </p>
                </div>
                <div id={`${id}-actions`} className={`tw-px-3 tw-my-4 tw-flex tw-gap-2 tw-justify-end`}>
                    <button data-testid="components-createclassdialog-button-4" 
                        id={`${id}-done-btn`}
                        onClick={onClose} 
                        className='samll-outline-btn'
                    >
                        Done
                    </button>
                    <button 
                        id={`${id}-submit-btn`} data-testid={`${id}-submit-btn`}
                        disabled={(!classProject?.class?.name || !classProject?.class?.type || !validateClassAndSubmodelNames(classProject?.class?.name) || disableButton)} 
                        onClick={handleOnCreateClick} 
                        className={`small-filled-btn`}
                    >
                        {selectedClass?.id !== null ? 'Update' : 'Create'}
                    </button>
                </div>
            </div>
            {showColors && (
                <div 
                    id={`${id}-color-picker`}
                    className="tw-fixed tw-top-1/2 tw-left-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 tw-w-[437px] tw-h-[331px] tw-px-2 tw-py-1 tw-bg-white tw-rounded tw-shadow tw-border tw-border-neutral-300"
                >
                    {COLORS.map((color, index) => (
                        <button data-testid={`components-createclassdialog-button-5-${index}`}
                            aria-label="Select colour"
                            id={`${id}-color-option-${index}`}
                            key={color + index}
                            style={{ backgroundColor: color, border: selectedItem === index ? '2px solid #0000FF' : 'none' }}
                            className={`tw-w-11 tw-h-11 tw-rounded tw-m-1`}
                            onClick={() => handleSelection(index)}
                        />
                    ))} 
                </div>
            )}
        </div>
    );
}



export default CreateClassDialog; 